import { z } from 'zod/v4'
import { ENTITY_ID_TYPES, ENTITY_KINDS } from './entities/vocabulary'
import { ENTITY_ATTRIBUTE_KEYS, IDENTIFIER_PROPERTIES } from './vocabulary/crossService'

// Mirrors `StepTypeId` in `server/workflowSteps/types.ts`, where the rationale for the removed step
// types lives — twelve alpha-era ones in MAR-97, and four more in MAR-487 when the converters became
// code-only tools. This list VALIDATES stored and submitted definitions, so a type absent from it is
// rejected rather than merely unexecutable.
export const WORKFLOW_STEP_TYPES = [
  'fill_document',
] as const

export const WORKFLOW_INPUT_TYPES = [
  'document_select',
  'text',
  'select',
  'multi_select',
  'date',
  'boolean',
  'number',
  // Pick an entity from the org's extracted parties (Parties feature, `useParties`) OR
  // enter it manually. Exposes the selected party's fields as `${key.name}` /
  // `${key.idNumber}` / `${key.address}` in the fill scope. See DOCUMENT-TEMPLATES.md.
  'party_select',
  // The blank .docx this workflow fills. NOT a step: resolution (org override ?? platform default)
  // is a platform rule, run once at run start into a documentId — see
  // `server/services/workflows/templateResolver.ts`. Carries its taxonomy in
  // `constraints.templateType` / `constraints.templateSubtype`.
  'template',
] as const

// Provider-qualified tiers — a Gemini-only step must be able to name its family; a neutral
// "fast"/"smart" would silently resolve to Claude. Mirrors `ModelTier` in
// server/workflowSteps/types.ts. The step that motivated the Gemini tiers was `pdf_to_docx`, which
// went with MAR-487; `flash`/`flash_lite` stay because stored definitions still name them and the
// distinction is a property of the providers, not of that one step.
export const WORKFLOW_MODELS = ['haiku', 'sonnet_4_6', 'flash_lite', 'flash'] as const

const optionSchema = z.object({
  value: z.string().min(1),
  label: z.string().min(1),
}).strip()



// ─── Workflow inputs (FLAT key namespace) ──────────────────────────────────────
// Input keys are a single flat, globally-unique namespace: steps reference them by
// `inputKey`, `forEach` iterates them, output bindings + `${key.field}` template vars
// and the run engine all resolve *by key*. Inputs stay a flat array for that reason —
// see `inputGroupSchema` below for why grouping is a presentation overlay, not nesting.
export const workflowInputSchema = z.object({
  key: z.string().min(1),
  label: z.string().min(1),
  type: z.enum(WORKFLOW_INPUT_TYPES),
  required: z.boolean(),
  description: z.string().optional(),
  // A real example shown *inside* the field (e.g. "las diez horas", "2025"). Distinct
  // from `description` (the helper line under the label) so the run form stops printing
  // the description twice — once as hint, once as placeholder.
  placeholder: z.string().optional(),
  constraints: z.object({
    maxDocuments: z.number().int().positive().optional(),
    /** Logical document type id (shared/documentTypes.ts) — typed slot. */
    documentType: z.string().min(1).optional(),
    /** @deprecated precursor to documentType; single raw contract_type value. */
    contractType: z.string().min(1).optional(),
    /** Restrict the entity slot picker to an entity kind (person, organization, property, vehicle).
     *  Omit to allow any kind. */
    partyKind: z.enum(ENTITY_KINDS).optional(),
    /**
     * **What real-world thing this input wants** (MAR-129) — the semantic axis, orthogonal to
     * `type`, which says only what widget to render. A `text` input asking for a DUI number is
     * `type: 'text'` + `entitySource.idType: 'national_id'`; the run form uses that to offer the
     * matching identifiers of whichever client or case the context rail has active.
     *
     * Nested as an object so the EAV attribute axis (`attributeKey`) can join it later without
     * touching a single stored definition. Omitting it leaves the input behaving exactly as before.
     *
     * The enum is the shared vocabulary — validated at publish time, so an unknown value fails when
     * the author publishes rather than mid-run in front of a user.
     */
    entitySource: z.object({
      idType: z.enum(ENTITY_ID_TYPES).optional(),
      /**
       * **Which party of the instrument this input describes** (MAR-176) — a free-text role name
       * chosen by the author (`trabajador`, `patrono`, `otorgante`). Every input sharing a role is
       * one PARTY BLOCK, and picking a party fills the whole block at once.
       *
       * Free text rather than an enum because the roles are the instrument's own vocabulary: a
       * finiquito has a trabajador and a patrono, an acta has a presidente and a secretario, and
       * enumerating them centrally would mean editing a shared list to author a new instrument.
       * The role is never shown to a model or stored on an entity — it only groups inputs.
       */
      role: z.string().min(1).optional(),
      /**
       * **Which fact of the chosen party fills this input.** Accepts flat property keys across all
       * entity kinds (`name`, `national_id`, `tax_id`, `given_names`, `age`, `registry_number`,
       * `plate`, etc.) as well as legacy `idNumber`, `attribute:<key>`, and `derived:<key>`.
       *
       * Only meaningful with `role`. Without it the input keeps the MAR-129 behaviour: suggest
       * identifiers under this one field, fill nothing else.
       */
      field: z.union([
        z.literal('name'),
        z.literal('idNumber'),
        z.literal('age'),
        z.enum(IDENTIFIER_PROPERTIES),
        z.enum(ENTITY_ATTRIBUTE_KEYS),
        z.string().regex(/^(attribute|derived):[a-z_]+$/, 'field must be a canonical entity property, "name", "idNumber", "attribute:<canonical_key>", or "derived:<key>"'),
      ]).optional(),
    }).strip().optional(),
    /** For `template`: the taxonomy (shared/templateTypes.ts) this slot fills. Authored, not
     *  chosen at run time — a poder-general workflow always fills the poder/general template. */
    templateType: z.string().min(1).optional(),
    templateSubtype: z.string().min(1).optional(),
    /** For `template`: an ADMIN-UPLOADED template, addressed by its row id. Uploaded
     *  templates have no taxonomy — identity is the row — so this is the alternative to
     *  `templateType`/`templateSubtype`, not an addition to it. */
    templateRowId: z.string().min(1).optional(),
    options: z.array(optionSchema).optional(),
    maxLength: z.number().int().positive().optional(),
    /** `text` inputs render as a single-line field by default; set true for a
     *  multi-line textarea (long prose like an attendee list or a résumé of results). */
    multiline: z.boolean().optional(),
  }).strip().optional(),
}).strip().superRefine((input, ctx) => {
  // A `template` slot without a taxonomy resolves to nothing, and the failure would surface as a
  // fill step receiving no source document — the class of late, confusing failure the entity gate
  // and this contract exist to move forward in time.
  if (input.type !== 'template') return
  const c = input.constraints
  const byTaxonomy = !!c?.templateType && !!c?.templateSubtype
  const byRowId = !!c?.templateRowId
  if (!byTaxonomy && !byRowId) {
    ctx.addIssue({
      code: 'custom',
      message: 'a `template` input must declare either constraints.templateRowId or constraints.templateType + templateSubtype',
      path: ['constraints', 'templateRowId'],
    })
  }
})

// ─── Input groups (UI PRESENTATION LAYER — references keys, does NOT own inputs) ─
// A group is how the RUN FORM lays inputs into sections; nothing else consumes it. It
// carries `inputKeys` (references into `inputs[]`), NOT nested `Input[]`, on purpose:
//   • Keys are already a flat, globally-unique namespace — nesting would NOT scope them,
//     it would just add a hierarchy every non-UI reader has to flatMap back out.
//   • ~50 sites (engine, resolveVariables, templating, step `inputKey` refs, publish
//     gates) resolve inputs by flat key and don't care about groups. Structure the data
//     around its common access (by-key), not its rarest (layout, one site).
//   • Groups are execution-irrelevant: they never enter run/RAG prompts, so they must not
//     sit on the critical path the engine reads.
//   • Backward compatible: no `inputGroups` → one implicit section (today's behavior).
//     Any input key not named by a group falls into an implicit catch-all section.
export const inputGroupSchema = z.object({
  key: z.string().min(1),
  label: z.string().min(1),
  description: z.string().optional(),
  /** References into `inputs[]`; also defines the field order within the section. */
  inputKeys: z.array(z.string().min(1)),
}).strip()

// Reference integrity: a group may only name keys that exist in `inputs`. A key left out
// of every group is fine (catch-all), but a dangling reference would render a ghost
// section entry — reject it here. Guarded so it no-ops on partial (draft-patch) payloads
// that carry `inputGroups` without `inputs`.
const inputGroupsRefine = (
  def: { inputs?: unknown; inputGroups?: unknown },
  ctx: z.RefinementCtx,
) => {
  const inputs = def.inputs as { key: string }[] | undefined
  const groups = def.inputGroups as { key: string, inputKeys: string[] }[] | undefined
  if (!Array.isArray(inputs) || !Array.isArray(groups)) return
  const known = new Set(inputs.map(i => i.key))
  for (const g of groups) {
    for (const k of g.inputKeys) {
      if (!known.has(k)) ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['inputGroups'],
        message: `Group "${g.key}" references unknown input key "${k}"`,
      })
    }
  }
}

// ─── Per-step iteration (forEach) ──────────────────────────────────────────────

export const stepForEachSchema = z.object({
  inputKey: z.string().min(1),
  as: z.string().min(1).optional(),
}).strip()

// ─── Per-row iteration (forEachRow, MAR-179) ───────────────────────────────────
// A sibling of `forEach`, not a variant of it — see `WorkflowStepDefinition.forEachRow` in
// server/workflowSteps/types.ts for the full rationale. Its shape is `{ inputKey, labelKey? }`,
// NOT `stepForEachSchema`'s `{ inputKey, as? }`: reusing `stepForEachSchema` here would `.strip()`
// away a submitted `labelKey` exactly like the missing field stripped `forEachRow` itself
// (AlexAmin/marcus#257).
export const stepForEachRowSchema = z.object({
  inputKey: z.string().min(1),
  labelKey: z.string().min(1).optional(),
}).strip()

const stepBaseSchema = z.object({
  index: z.number().int().min(0),
  name: z.string().min(1),
  model: z.enum(WORKFLOW_MODELS),
  description: z.string().optional(),
  forEach: stepForEachSchema.optional(),
  forEachRow: stepForEachRowSchema.optional(),
}).strip()

// ─── Uniform source reference (input side) ─────────────────────────────────────

export const stepSourceRefSchema = z.discriminatedUnion('from', [
  z.object({ from: z.literal('input'), inputKey: z.string().min(1) }).strip(),
  z.object({ from: z.literal('previous_step') }).strip(),
  z.object({ from: z.literal('step'), index: z.number().int().min(0) }).strip(),
  z.object({ from: z.literal('all_prior_steps') }).strip(),
  // READ-ONLY (MAR-428). `reference_material` is no longer offered by the source picker: nothing
  // writes `definition_snapshot.referenceMaterials`, so the engine resolves it against `[]` on
  // every run. It stays HERE, and in `server/workflowSteps/types.ts`, because definitions saved
  // before that removal may still carry it and must still parse. Do not re-add it to the picker.
  z.object({ from: z.literal('reference_material'), ref: z.string().min(1) }).strip(),
])

// ─── Output file binding (output side) ─────────────────────────────────────────

export const outputFileBindingSchema = z.object({
  outputKey: z.string().min(1),
  label: z.string().min(1),
  defaultName: z.string().min(1),
  saveAsDocument: z.boolean(),
}).strip()













const fillDocumentConfigSchema = z.object({
  source: stepSourceRefSchema.describe('La plantilla .docx a rellenar (normalmente el paso `template` anterior: {from:"step",index:0}).'),
  instruction: z.string().min(1).describe('Los datos con que rellenar los marcadores. Admite ${parametro} / ${slot.campo}.'),
  outputFile: outputFileBindingSchema.optional(),
}).strip()

/**
 * ONE MEMBER, AND THE UNION STAYS A UNION (MAR-487).
 *
 * `replace_party`, `image_to_docx`, `pdf_to_docx` and `calculate_severance` were removed here with
 * their config schemas when the converters became code-only tools. Keeping them was not the
 * conservative choice it looked like: `lenientStepSchema` below has always taken its type from
 * `WORKFLOW_STEP_TYPES`, which lists `fill_document` alone, so a `replace_party` step could not be
 * DRAFT-SAVED while this union still let it be PUBLISHED — the permissive path was the stricter one.
 *
 * UNLIKE `reference_material` ABOVE, THIS IS SAFE TO NARROW. That one had to stay because stored
 * definitions still carry it and must parse; these schemas are reached only by the two write paths
 * (`POST /api/workflows`, `PUT /api/workflows/:id`). No read path parses a stored definition through
 * them, so archived definitions naming a removed type are still readable history — and migration
 * 0136 archives any that were left published.
 */
export const workflowStepSchema = z.discriminatedUnion('type', [
  stepBaseSchema.extend({ type: z.literal('fill_document'), config: fillDocumentConfigSchema }),
])

export type WorkflowStep = z.infer<typeof workflowStepSchema>

// ─── Required content fields per step type ──────────────────────────────────────
//
// The `min(1)` content fields of each step's config schema, in one place. The client
// builder imports this to flag an incomplete step inline (instead of failing only at
// publish). Run-time fields (resolved in the run form, not the builder) and optional
// fields are intentionally excluded. Keep this in sync with the `.min(1)` fields above.
export const REQUIRED_STEP_FIELDS: Record<string, string[]> = {
  fill_document: ['instruction'],
}

// ─── Reserved (auto-managed) input slot keys ────────────────────────────────────
/** Input slots whose keys are fixed and not user-renamable/removable. */
export const RESERVED_INPUT_KEYS = ['documents', 'contexto_adicional', 'template'] as const

/** Is `key` one of the reserved (auto-managed) input slot keys? Accepts any string. */
export function isReservedInputKey(key: string): boolean {
  return (RESERVED_INPUT_KEYS as readonly string[]).includes(key)
}

// ─── Terminal output-step rule (§3.4) ──────────────────────────────────────────

/** Output-category step types — a valid workflow must terminate in one of these. */
export const TERMINAL_STEP_TYPES = ['fill_document'] as const

export function isTerminalStep(step: { type?: string } | null | undefined): boolean {
  return !!step && (TERMINAL_STEP_TYPES as readonly string[]).includes(step.type ?? '')
}

/** Returns an error message if the steps do not terminate in an output step, else null. */
export function assertValidTerminalStep(steps: { type?: string }[] | null | undefined): string | null {
  if (!steps || steps.length === 0) return 'Workflow must have at least one step'
  const last = steps[steps.length - 1]
  if (!isTerminalStep(last)) {
    return 'Workflow must end in a step that produces a document (fill_document)'
  }
  return null
}

// ─── Output manifest (§3.3) ────────────────────────────────────────────────────

export type OutputFileBinding = z.infer<typeof outputFileBindingSchema>

/**
 * Collect the output manifest = the union of every step's outputFile binding.
 *
 * Accepts any step-shaped objects (parsed `WorkflowStep`s from a published
 * definition AND the looser `BuilderStep`s the run/builder UI holds), so it reads
 * `config.outputFile` structurally and validates each candidate through
 * `outputFileBindingSchema` rather than trusting the caller's static type.
 */
export function collectOutputBindings(
  steps: ReadonlyArray<{ config?: unknown } | null | undefined> | null | undefined,
): OutputFileBinding[] {
  const bindings: OutputFileBinding[] = []
  for (const step of steps ?? []) {
    const config = step?.config
    if (!config || typeof config !== 'object') continue
    const parsed = outputFileBindingSchema.safeParse((config as { outputFile?: unknown }).outputFile)
    if (parsed.success) bindings.push(parsed.data)
  }
  return bindings
}

const triggerSchema = z.object({
  type: z.literal('file_uploaded'),
  config: z.object({
    fileTypes: z.array(z.string()).optional(),
  }).strip(),
}).strip()

const runtimeParameterSchema = z.object({
  key: z.string().min(1),
  label: z.string().min(1),
  type: z.literal('text'),
  default: z.string().optional(),
}).strip()

const baseWorkflowDefinitionSchema = z.object({
  name: z.string().trim().min(1),
  description: z.string().nullable().optional(),
  visibility: z.enum(['private', 'shared', 'unlisted']).default('private'),
  category: z.string().nullable().optional(),
  tags: z.array(z.string()).default([]),
  steps: z.array(workflowStepSchema).default([]),
  inputs: z.array(workflowInputSchema).default([]),
  // Optional presentation-only sectioning for the run form; omit for the flat form.
  inputGroups: z.array(inputGroupSchema).default([]),
  triggers: z.array(triggerSchema).default([]),
  parameters: z.array(runtimeParameterSchema).default([]),
}).strip()

export const workflowDefinitionCreateSchema = baseWorkflowDefinitionSchema.superRefine(inputGroupsRefine)

// ─── Lenient step schema (draft save) ──────────────────────────────────────────
// Draft save is permissive (§3.4): an author can save a half-built workflow whose
// steps still have empty prompts/instructions or incomplete config. Structure is
// kept (index/type/model) but the config is passed through loosely. Full content
// validation (min(1) prompts, StepSourceRef shapes, terminal-step rule) is enforced
// only at PUBLISH via `workflowStepSchema` / `workflowDefinitionPublishSchema`.
const lenientStepSchema = z.object({
  index: z.number().int().min(0),
  name: z.string().default(''),
  type: z.enum(WORKFLOW_STEP_TYPES),
  model: z.enum(WORKFLOW_MODELS),
  description: z.string().optional(),
  forEach: stepForEachSchema.optional(),
  forEachRow: stepForEachRowSchema.optional(),
  config: z.record(z.string(), z.unknown()).default({}),
}).strip()

export const workflowDefinitionUpdateSchema = z.object({
  name: z.string().trim().min(1).optional(),
  description: z.string().nullable().optional(),
  visibility: z.enum(['private', 'shared', 'unlisted']).optional(),
  category: z.string().nullable().optional(),
  tags: z.array(z.string()).optional(),
  steps: z.array(lenientStepSchema).optional(),
  inputs: z.array(workflowInputSchema).optional(),
  inputGroups: z.array(inputGroupSchema).optional(),
  triggers: z.array(triggerSchema).optional(),
  parameters: z.array(runtimeParameterSchema).optional(),
}).strip().refine(
  body => Object.keys(body).length > 0,
  { message: 'At least one workflow field is required' },
).superRefine(inputGroupsRefine)

const publishObjectSchema = baseWorkflowDefinitionSchema.extend({
  steps: z.array(workflowStepSchema).min(1),
})

// The refines below receive the schema's PARSED output (steps is the WorkflowStep
// discriminated union). Type their param to that exact shape so it matches what zod
// passes — the helpers read only the loose `{ type?, config? }` fields, which the parsed
// steps satisfy. (The chat-tool confirmation envelopes that extended this were removed with the
// create_workflow / update_workflow tools — MAR-97.) (was: extends this with `reason`, which is
// assignable to this type, so the same refines apply there.)
type ParsedWorkflowDefinition = z.infer<typeof publishObjectSchema>

const terminalStepRefine = (def: ParsedWorkflowDefinition) =>
  assertValidTerminalStep(def.steps) === null
const terminalStepRefineOpts = {
  message: 'Workflow must end in an output step (fill_document)',
  path: ['steps'],
}

/** Every step's `outputFile.outputKey` must be unique — run-form output names are
 *  keyed by it, so duplicates collide silently. */
const uniqueOutputKeysRefine = (def: ParsedWorkflowDefinition) => {
  const keys = collectOutputBindings(def.steps).map(b => b.outputKey)
  return new Set(keys).size === keys.length
}
const uniqueOutputKeysRefineOpts = {
  message: 'Each output file must have a unique key (a duplicated step copied its outputKey)',
  path: ['steps'],
}

export const workflowDefinitionPublishSchema = publishObjectSchema
  .refine(terminalStepRefine, terminalStepRefineOpts)
  .refine(uniqueOutputKeysRefine, uniqueOutputKeysRefineOpts)
  .superRefine(inputGroupsRefine)

export type WorkflowDefinitionCreateInput = z.infer<typeof workflowDefinitionCreateSchema>
export type WorkflowDefinitionUpdateInput = z.infer<typeof workflowDefinitionUpdateSchema>
export type WorkflowDefinitionPublishInput = z.infer<typeof workflowDefinitionPublishSchema>
